/**
 * Human labels for every domain enum, in one place.
 *
 * The API speaks in codes. People do not. Pages import from here so an
 * enrollment state reads the same on the campaign page, the queue and the
 * dashboard, and so no raw code ever leaks into the interface.
 *
 * Two levels of copy, one table:
 *
 *   - `label.x()` is the short name that goes in a badge or a filter.
 *   - the exported tables carry a `detail` sentence as well, which is what a
 *     panel shows when it has room to explain the state rather than name it.
 *
 * `errors.ts` reads the same tables, so the sentence explaining why an action
 * was refused and the sentence beside the badge on the record can never drift
 * into saying two different things.
 *
 * Copy rules: sentence case, no em dashes, no semicolon-joined clauses, and
 * never an internal identifier.
 */
// Type-only, so nothing from the server kernel reaches the browser bundle.
// `EnabledChannel` is not re-exported by `./types`, and it is the honest key
// for the two per-channel tables below: a key there is an offer, and the type
// is what stops a pinterest row compiling in by accident.
import type { EnabledChannel } from '@gojibasel/shared'
import { COPY_BEARING_ACTIONS, MESSAGED_CHANNELS, MESSAGED_SCOPES, resolveMessagedCheck, ROUTED_ACTIONS } from '@gojibasel/shared/types'
import {
  FIELD_CHECK_FIELDS,
  FIELD_CHECK_OPS,
  customFieldKeyOf,
  resolveProspectCheck,
  type NamedFieldCheckField,
  type FieldCheckOp,
  type ProspectCheckKeys,
} from '@gojibasel/shared/prospect-checks'
import type {
  AccountObservedState,
  AccountReach,
  ActionType,
  ApprovalState,
  AssetType,
  Autonomy,
  CampaignStatus,
  Channel,
  ClassificationLabel,
  DispatchTarget,
  DslCondition,
  EffectiveAccountHealth,
  EmailProvider,
  EnrollmentState,
  KnownOperatorActionResult,
  MailboxKind,
  MessagedChannel,
  MessagedScope,
  ObservationSource,
  ObservationType,
  OperatorActionMode,
  OperatorActionState,
  PolicyScope,
  ProspectStatus,
  QuotaDeniedReason,
  SignalType,
  SuppressionReason,
  UserRole,
} from './types'

/** Visual tone shared by Badge, Pill, Meter and the status dots. */
export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent'

/** A state with the sentence that explains it, for panels with room to speak. */
export interface StateCopy {
  label: string
  /** One sentence a non-technical person can act on. */
  detail: string
  tone: Tone
}

export const CHANNEL: Record<Channel, string> = {
  linkedin: 'LinkedIn',
  email: 'Email',
  instagram: 'Instagram',
  x: 'X',
  facebook: 'Facebook',
  // Its own channel since 2026-09-12. Until then `facebook` was the Messenger
  // app wearing the family name, because Messenger was the only measured driver
  // in it. Both are measured now, so both get the word people already use.
  messenger: 'Messenger',
  // Brand casing the humanizeCode fallback cannot produce: it would say
  // "Whatsapp" on every badge if this entry were ever dropped.
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  pinterest: 'Pinterest',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  threads: 'Threads',
  mastodon: 'Mastodon',
  reddit: 'Reddit',
  bluesky: 'Bluesky',
}

/**
 * The same channels as they are said inside a sentence.
 *
 * Every one of them is a proper noun except email, and "Choose which Email
 * accounts this campaign sends from" reads as a mistake on every screen that
 * builds a sentence around a channel.
 */
export const CHANNEL_WORD: Record<Channel, string> = { ...CHANNEL, email: 'email' }

/**
 * The channels this build can run, in the order the interface shows them.
 * Pinterest alone stays out: nothing was measured on it, so nothing offers it.
 * Typed as `Channel[]` rather than `EnabledChannel[]` because filter code asks
 * `includes(someChannel)` about channels that arrive off the wire.
 */
export const ENABLED_CHANNEL_ORDER: Channel[] = [
  'linkedin',
  'email',
  'instagram',
  'x',
  'facebook',
  'messenger',
  'whatsapp',
  'telegram',
  // 2026-10-15, one verb each, a comment under a post named by its link.
  'threads',
  'mastodon',
  'youtube',
  'tiktok',
  // 2026-10-17, the same one verb.
  'reddit',
  'bluesky',
]

export const ACTION_TYPE: Record<ActionType, StateCopy> = {
  connection_request: {
    label: 'Connection request',
    detail: 'Ask to connect, with a short note if you want one.',
    tone: 'accent',
  },
  add_friend: {
    label: 'Add friend',
    detail: 'Ask to be friends. That is how a connection opens on this app.',
    tone: 'accent',
  },
  // THE BASE SENTENCE SAYS ONLY WHAT IS TRUE ON ALL SEVEN APPS, and each app's
  // own condition lives in `ACTION_DETAIL_BY_CHANNEL` below.
  //
  // This entry has now been wrong twice in the same way. It first said "someone
  // you are already connected to", which is LinkedIn's rule; that was replaced
  // with "On LinkedIn this needs an existing connection first", which is the
  // same rule with the app named, and the palette prints it under the X,
  // Facebook, Messenger, WhatsApp, Telegram and Instagram headings too. A reader
  // picking a WhatsApp block was told about LinkedIn connections. The lesson is
  // that a verb offered on seven apps cannot carry one app's condition at all,
  // however carefully the sentence is hedged.
  dm: {
    label: 'Direct message',
    detail: 'Send a direct message. Whether it reaches their inbox or a request folder is the app\'s rule, not ours.',
    tone: 'accent',
  },
  inmail: {
    label: 'InMail',
    detail: 'A message with a subject line to somebody you are not connected to. Needs LinkedIn Premium or Sales Navigator on the account.',
    tone: 'info',
  },
  // Neutral, because only LinkedIn shows a person who looked at their profile.
  // Instagram and Facebook both offer this verb and neither tells the person,
  // so the old sentence promised an effect that does not happen on two of the
  // three apps it was printed under.
  profile_view: {
    label: 'Profile view',
    detail: 'Open their profile and look at it.',
    tone: 'neutral',
  },
  follow: { label: 'Follow', detail: 'Follow them without asking to connect.', tone: 'neutral' },
  like_post: { label: 'Like a post', detail: 'React to something they posted recently.', tone: 'neutral' },
  // Deliberately worded so the difference from the line above is visible at a
  // glance in a list where both appear. "Like" sets the default; this one picks.
  react_post: {
    label: 'React to a post',
    detail: 'Choose which reaction to leave, rather than a plain like.',
    tone: 'neutral',
  },
  // Two entries a step above and a step below the one before them, so the
  // detail has to separate three verbs that all contain the word "post". The
  // rule the copy follows is that each says WHERE it happens, because that is
  // the real difference: one reacts to a post somebody opened, one walks to a
  // person's own activity, one leaves words under it.
  //
  // No number in either sentence, deliberately. How many posts a visit reacts
  // to is a setting an operator can change, so a label promising three to five
  // would go stale the first time anybody did.
  like_posts: {
    label: 'Like their recent posts',
    detail: 'Open their profile, go to their activity, and react to a few of the posts they wrote.',
    tone: 'neutral',
  },
  // `accent` and not `neutral`, unlike every other engagement verb. That tone
  // is what a badge uses to say "this one goes out", and this one does: the
  // words are public, attributed, and read by everybody who scrolls past.
  // Styling it as quietly as a like would have the interface understate the
  // only verb here a stranger can read.
  comment_post: {
    label: 'Comment on a post',
    detail: 'Leave a public comment on their most recent post. Anyone who reads the post can see it.',
    tone: 'accent',
  },
  // `accent` for the comment's reason and more: a repost goes out on OUR own
  // timeline, in front of everybody who follows the account. The detail says it
  // may do nothing, because most visits find no post that clears the bar.
  repost_post: {
    label: 'Repost their best post',
    detail: 'Open their profile and repost one of their posts to this account, only when one is strong enough. Everyone who follows this account sees it.',
    tone: 'accent',
  },
  // `accent` for the repost's reason: it goes on our own timeline, in front of
  // everybody who follows the account (0168).
  post_tweet: {
    label: 'Post on X',
    detail: 'Publish a new post from this account: words, one picture or clip, or both. Everyone who follows this account sees it.',
    tone: 'accent',
  },
  withdraw_request: {
    label: 'Withdraw request',
    detail: 'Take back a connection request nobody answered.',
    tone: 'neutral',
  },
  list_conversations: {
    label: 'List conversations',
    detail: 'Pull the open threads for this account.',
    tone: 'neutral',
  },
  inbox_check: {
    label: 'Inbox check',
    detail: 'Sweep the inbox and mark which threads got a reply.',
    tone: 'info',
  },
  connections_check: {
    label: 'Connections check',
    detail: 'Confirm which pending requests were accepted.',
    tone: 'info',
  },
  send: { label: 'Send email', detail: 'Send an email through the connected mailbox.', tone: 'accent' },
  // The two silent saves, one word per app because neither app has the
  // other's word. They touch nobody: both are private, notify nobody and are
  // reversible without trace, and the copy says that outright because it is
  // the whole reason an author or an operator would reach for one first.
  // Since the 2026-08-13 channel widening a step on X or Instagram can carry
  // one like any other measured action.
  bookmark: {
    label: 'Bookmark a post',
    detail: 'Save a post to this account bookmarks on X. Nobody is contacted and nobody is told.',
    tone: 'neutral',
  },
  save_post: {
    label: 'Save a post',
    detail: 'Save a post to a private collection on Instagram. Nobody is contacted and nobody is told.',
    tone: 'neutral',
  },
  profile_scan: {
    label: 'Read a public profile',
    detail: 'Read what somebody published about themselves: their bio, and their posts where the app shows them. Visible to the person, like any profile view.',
    tone: 'neutral',
  },
  // The only entry in this table for something that reaches nobody, and the
  // detail has to carry that or the label alone reads like one more way to
  // contact a person. "Save" beside fourteen verbs that all touch somebody is
  // exactly the word a tired operator misreads, so the sentence says what does
  // not happen as plainly as what does.
  //
  // Tone is neutral for the same reason. `accent` is the outreach verbs' tone
  // and it is what a badge uses to say "this one goes out", so an accent badge
  // here would be the styling making the claim the copy refuses to.
  save_contact: {
    label: 'Save to the phone',
    detail: "Write their name and number into the phone's own address book. It sends them nothing and it puts them in no campaign.",
    tone: 'neutral',
  },
  idle_browse: {
    label: 'Idle browse',
    detail: 'Open the app, scroll the feed, touch nothing and close it. It reaches nobody and records nothing about anyone. It exists so the account does something other than outreach.',
    tone: 'neutral',
  },
  company_people_search: {
    label: 'Find on company page',
    detail: "Look for this person on their company's LinkedIn People tab after their profile link stopped working. It sends them nothing.",
    tone: 'neutral',
  },
  post_analytics: {
    label: 'Read post analytics',
    detail: "Read the account's own post analytics, open the posts that gained reactions or comments, and note who they came from. It sends nobody anything.",
    tone: 'neutral',
  },
  like_comment: {
    label: 'Like their comment',
    detail: "Like the comment this person left on one of the account's own posts.",
    tone: 'accent',
  },
  reply_comment: {
    label: 'Reply to their comment',
    detail: "Reply under the comment this person left on one of the account's own posts, with words written for that comment and approved.",
    tone: 'accent',
  },
}

/**
 * Every action a channel performs, which is also everything it can be metered
 * on. This mirrors the one per-channel table the rest of the system resolves
 * against, so the limits screen and the workflow editor cannot end up offering
 * a different vocabulary from the one the platform actually honours.
 *
 * It is mirrored rather than imported because the browser takes types from the
 * kernel and never values: the kernel's entry point reaches Redis, the queue
 * and the config loader, none of which belong in a page. `labels.test.ts` pins
 * this table against the shared one in both directions, so a verb added or
 * dropped on either side turns that test red instead of quietly changing what
 * these screens offer.
 *
 * Every channel this build can run is keyed here and pinterest alone is not,
 * which is the point rather than an omission. A key is an offer: it puts a
 * limit to set, and a step to write, in front of somebody. Pinterest gets one
 * only when a device has been seen to carry something out on it.
 */
export const METERED_ACTIONS_BY_CHANNEL: Record<EnabledChannel, readonly ActionType[]> = {
  linkedin: [
    'connection_request',
    'dm',
    'inmail',
    'profile_view',
    'follow',
    'like_post',
    // Beside `like_post` and in the kernel's own order, so the mirror test can
    // compare the two rows as sets and a reader can see the three post verbs
    // together rather than hunting for them.
    'like_posts',
    'comment_post',
    'withdraw_request',
    'list_conversations',
    'inbox_check',
    'connections_check',
    'save_contact',
    'idle_browse',
    // The People tab search after a broken link. Never authored, see AUTHORABLE_ACTIONS.
    'company_people_search',
    // The daily read of the account's own post analytics (0158). Never authored either.
    'post_analytics',
    // One comment under the account's own post, liked or replied to (0159).
    'like_comment',
    'reply_comment',
  ],
  email: ['send'],
  // The four device rows mirror what the engine measured on the phone,
  // outreach verbs first and the sweeps last, the way the LinkedIn row reads.
  // `save_post` and `bookmark` are the same idea in two apps' words, and each
  // sits only on the app whose control was measured.
  //
  // `save_contact` is last on every handset row and on no other row, and its
  // position is the point rather than tidiness. It belongs to the phone rather
  // than to the app: the same address book sits behind all six of these
  // identities, so the verb appears six times here and names one book. Email
  // has no handset behind it and therefore no book, which is why that row is
  // the one that does not gain it.
  // `like_posts` and `comment_post` joined 2026-09-10. This row is a hand-written
  // mirror of CHANNEL_ACTION_TYPES in the kernel and labels.test.ts pins the two
  // against each other in both directions, so it is a build gate rather than
  // something to remember.

  // `idle_browse` is last on every handset row and on no other, beside
  // `save_contact` and for the same reason: it belongs to the phone rather than
  // to the app. Opening an app and scrolling it is a gesture on the device the
  // apps sit on, which is why email, with no handset behind it, does not gain it.
  instagram: ['dm', 'follow', 'profile_view', 'profile_scan', 'save_post', 'list_conversations', 'inbox_check', 'save_contact', 'like_posts', 'comment_post', 'idle_browse'],
  // `follow` and `repost_post` joined 2026-09-25 with the kernel row, for the
  // owner's X routine.
  // `post_tweet` joined 2026-10-11 (0168), asked for from Run an action only.
  x: ['dm', 'bookmark', 'profile_scan', 'list_conversations', 'inbox_check', 'save_contact', 'like_posts', 'comment_post', 'idle_browse', 'follow', 'repost_post', 'post_tweet'],
  // Platform channel `facebook` is the Messenger app, and `add_friend` is its
  // connection request.
  // The Facebook app since the rename. It reaches a conversation through a
  // person's profile rather than through a list, so the two sweeps that used to
  // sit on this row went to `messenger` with the app that has an inbox.
  // `profile_scan` joined 2026-09-14, which is what lets a Facebook account be
  // kept for reading and picked as the Facebook scout.
  // `dm` removed 2026-09-20 with the kernel row. Facebook's Message control opens
  // Messenger's chat on Messenger's inbox, so a Facebook DM step and a Messenger
  // DM step were two ways to write into one conversation. The verb stays on
  // `messenger`, which is the app that has the inbox.
  // `like_posts` joined 2026-09-25 with the kernel row, the walk to the person's
  // own posts by profile number that the engine's Facebook driver already had.
  // `comment_post` joined 2026-10-17 with the kernel row: a comment under one of
  // the person's own posts, reached the way the like walk reaches them.
  facebook: ['add_friend', 'like_post', 'react_post', 'profile_view', 'profile_scan', 'save_contact', 'like_posts', 'comment_post', 'idle_browse'],
  // The row that was called `facebook` until 2026-09-12, unchanged verb for verb.
  messenger: ['add_friend', 'dm', 'list_conversations', 'inbox_check', 'save_contact', 'idle_browse'],
  // The row is short because the app is short. The verbs missing here are
  // absent concepts, not unmeasured gaps: WhatsApp has no follow, no
  // connection request and no public post, and there is no connections_check
  // because nothing on WhatsApp is ever accepted, so a sweep would have
  // nothing to confirm.
  whatsapp: ['dm', 'list_conversations', 'inbox_check', 'save_contact', 'idle_browse'],
  // One verb the app performs, and for a reason none of the rows above share:
  // Telegram's conversation list publishes no names, so there is no sweep to
  // offer. The second entry is the handset's, not Telegram's.
  telegram: ['dm', 'save_contact', 'idle_browse'],
  // One verb each since 2026-10-15, a comment under a post or video named by its
  // link. No save_contact: none of the three reaches anybody through an address book.
  youtube: ['comment_post'],
  // Threads and Reddit read their inboxes since 2026-10-28, read only.
  threads: ['comment_post', 'list_conversations', 'inbox_check'],
  mastodon: ['comment_post'],
  tiktok: ['comment_post', 'list_conversations', 'inbox_check'],
  reddit: ['comment_post', 'list_conversations', 'inbox_check'],
  // Bluesky reads and answers its chats since 2026-10-29.
  bluesky: ['comment_post', 'list_conversations', 'inbox_check', 'dm'],
}

/**
 * Whether a person writes this action into a workflow, or the platform
 * schedules it for itself.
 *
 * NOT THE SAME QUESTION AS HOUSEKEEPING, since 2026-09-22. They were the same
 * set until `idle_browse` arrived: housekeeping in the kernel means EXEMPT FROM
 * THE PACE CLOCK, which is right for the address book write, which touches no
 * app, and wrong for an idle cycle, which IS the app and must read and stamp
 * the clock a DM reads. It is still not something anybody puts in a sequence.
 * Every housekeeping verb is unauthorable; not every unauthorable verb is
 * housekeeping.
 *
 * The false rows are the three sweeps that read back who replied and who
 * accepted. They cost allowance like anything else, so they belong on the
 * limits screen, but the scheduler raises them itself and nobody puts one in
 * a sequence.
 *
 * The two silent saves used to be false as well, from before their apps could
 * run. The 2026-08-13 widening enabled X and Instagram, so a method step on
 * those channels can carry them like any other measured action, and they are
 * authorable now. `add_friend` is authorable the way it always was, and the
 * widening is what finally put a Messenger key in the table above for it to
 * appear under.
 *
 * Exhaustive on purpose. A new action type will not compile until somebody
 * classifies it, and that is the moment to decide whether an author may reach
 * it at all.
 */
export const AUTHORABLE_ACTIONS: Record<ActionType, boolean> = {
  connection_request: true,
  add_friend: true,
  dm: true,
  inmail: true,
  profile_view: true,
  follow: true,
  like_post: true,
  // Authorable, and it is the reason the action exists as a separate word: an
  // author who wants Love rather than Like has to be able to say so in a step.
  react_post: true,
  // Both authorable, and this is the flag the whole feature turns on: the
  // operator asked to be able to put these in a playbook, and a false here is
  // exactly what would stop them. Neither is a sweep, which is the one thing
  // this table has ever been false for on a channel verb: both are aimed at a
  // named prospect and both are what a sequence is made of.
  like_posts: true,
  comment_post: true,
  // Authorable, for the same reason as the pair above: the owner asked for it in
  // his X routine, and it is aimed at a named prospect.
  repost_post: true,
  // Never authored. A post is about the account, not a person on a list, so a
  // person asks for one from Run an action (0168).
  post_tweet: false,
  withdraw_request: true,
  list_conversations: false,
  inbox_check: false,
  connections_check: false,
  send: true,
  bookmark: true,
  save_post: true,
  // Authorable, and the first cut of this had it wrong. It carries no words for
  // an author to compose, but neither does `profile_view`, which is authorable:
  // this flag decides what a SEQUENCE may contain, and whether a step carries
  // copy is `copyOnCard`. The rule the mirror test states is "everything the
  // channel does except the sweeps", and this is not a sweep -- the sweeps read
  // the account's own surfaces and the scheduler raises them, while this is
  // aimed at a named prospect.
  //
  // It is also the most useful thing to be able to author: read what somebody
  // published, then write to them with it in hand.
  profile_scan: true,
  // False, and not for the reason the three sweeps above are false. Those are
  // account-level reads the scheduler raises for itself. This one is aimed at a
  // named person and could be described as a step, and the reason it is still
  // not authorable is that a sequence is a thing that reaches somebody over
  // time, and this reaches nobody at any point. A step in a campaign that wrote
  // an address book row would spend a slot in a sequence, appear in the
  // enrollment history beside messages that were sent, and report progress in a
  // conversation that had not started.
  //
  // Where it IS asked for is the prospect screens, one person or a selection,
  // as its own action with its own record. That is a different surface with a
  // different meter, and keeping it off the authoring list is what keeps the
  // two from being confused for one another.
  save_contact: false,
  // NOT AUTHORABLE, for a sharper reason than save_contact's.
  //
  // That one is about a person and belongs on their screen. This one is about
  // NOBODY. A step in a sequence is a thing that happens to somebody, and an
  // idle cycle happens to the account. Putting it in a workflow would spend a
  // slot in somebody's sequence, sit in their enrollment history beside the
  // messages they were actually sent, and report progress on a conversation it
  // has nothing to do with.
  //
  // It is raised by a sweeper against an account instead, which is the surface
  // that matches what it is.
  idle_browse: false,
  // Never authored. The platform runs it itself, once, when a LinkedIn profile link breaks.
  company_people_search: false,
  // Never authored. The daily sweep asks for it, at a time drawn per account and day (0158).
  post_analytics: false,
  // Steps of a sequence for the people a post analytics read found (0159, 0160). The
  // scheduler finds the comment each answers under the sending account's own posts and
  // skips the step when there is none, so somebody who only reacted goes straight on.
  like_comment: true,
  reply_comment: true,
}

const authorable = (actions: readonly ActionType[]): readonly ActionType[] =>
  actions.filter((action) => AUTHORABLE_ACTIONS[action])

/** Actions you can actually put in a workflow, per channel. Derived, never listed twice. */
/**
 * What a verb means ON ONE APP, where the neutral sentence is not the whole story.
 *
 * WHY THIS TABLE EXISTS. `ACTION_TYPE` is keyed on the verb alone, and seven of
 * those verbs are offered on more than one app. The palette groups by app and
 * prints the verb's sentence under every heading, so any app-specific clause in
 * there is a false statement under the other six. That happened twice with `dm`
 * and once with `profile_view`, both shipped, both reported by a person reading
 * the palette rather than caught by a test.
 *
 * The rule: `ACTION_TYPE[action].detail` says only what is true everywhere the
 * verb appears, and anything narrower goes here under the app it is true of.
 * A missing entry is not a gap, it means the neutral sentence is already right.
 */
export const ACTION_DETAIL_BY_CHANNEL: Partial<
  Record<EnabledChannel, Partial<Record<ActionType, string>>>
> = {
  linkedin: {
    dm: 'Send a direct message. On LinkedIn this needs an existing connection first.',
    profile_view: 'Look at their profile. LinkedIn shows them you were there.',
  },
  instagram: {
    dm: 'Send a direct message. Someone who does not follow back gets it as a message request.',
    // The owner's Instagram visit of 2026-09-28. How many posts, whose comments,
    // the link and the story are drawn per visit from the Instagram rows in Safety controls.
    profile_view:
      'Look through their profile the way a person does: their posts and the comments on some, their reels, reposts when public, tagged photos, their story, and often a link to see what they are about. How much is set in Safety controls. Instagram does not tell them.',
  },
  x: {
    dm: 'Send a direct message. It only arrives if their settings accept messages from strangers.',
    follow: 'Follow them on X. They are told, and their posts start showing on this account.',
  },
  facebook: {
    // Facebook's Message control opens Messenger's chat on Messenger's inbox, so a
    // message written here is sent from the Messenger account, to their Facebook
    // profile, and lands in that same thread.
    dm: 'Send a message on Facebook. It is sent through Messenger, from your Messenger account, and lands in the same thread.',
    add_friend: 'Ask to be friends on Facebook. That is how a connection opens there.',
  },
  messenger: {
    dm: 'Send a direct message. Someone who is not a friend gets it as a message request.',
    add_friend: 'Ask to be friends on Messenger. That is how a connection opens there.',
  },
  whatsapp: {
    dm: 'Send a direct message. It goes to their phone number, so there is nothing to connect first.',
  },
  telegram: {
    dm: 'Send a direct message. It goes to their username, so there is nothing to connect first.',
  },
  bluesky: {
    dm: 'Send a direct message in Bluesky chat. It reaches them only if they already chat with this account or take chats from it.',
  },
}

/**
 * The sentence to show for a verb, on the app it is being shown under.
 *
 * Every surface that knows the channel uses this. One that genuinely does not
 * may read `ACTION_TYPE[action].detail`, and will get the neutral sentence,
 * which is the honest answer when the app is unknown.
 */
export function actionDetailFor(action: ActionType, channel: Channel | string): string {
  const perChannel = ACTION_DETAIL_BY_CHANNEL[channel as EnabledChannel]
  return perChannel?.[action] ?? ACTION_TYPE[action].detail
}

export const ACTIONS_BY_CHANNEL: Record<EnabledChannel, readonly ActionType[]> = {
  linkedin: authorable(METERED_ACTIONS_BY_CHANNEL.linkedin),
  email: authorable(METERED_ACTIONS_BY_CHANNEL.email),
  instagram: authorable(METERED_ACTIONS_BY_CHANNEL.instagram),
  x: authorable(METERED_ACTIONS_BY_CHANNEL.x),
  // A message on Facebook is authored here and sent through Messenger, the app that
  // owns the thread, so it is offered on Facebook without being metered there. The
  // campaign program turns it into a Messenger message (stepChannelFor).
  facebook: [...authorable(METERED_ACTIONS_BY_CHANNEL.facebook), ...(ROUTED_ACTIONS.facebook ?? [])],
  messenger: authorable(METERED_ACTIONS_BY_CHANNEL.messenger),
  // Derivation leaves exactly one verb, dm, because the row is dm plus the
  // two sweeps. That is the whole authoring surface WhatsApp has.
  whatsapp: authorable(METERED_ACTIONS_BY_CHANNEL.whatsapp),
  // Derivation leaves dm, because dm is the whole row. Telegram's conversation
  // list publishes nothing to sweep.
  telegram: authorable(METERED_ACTIONS_BY_CHANNEL.telegram),
  youtube: authorable(METERED_ACTIONS_BY_CHANNEL.youtube),
  threads: authorable(METERED_ACTIONS_BY_CHANNEL.threads),
  mastodon: authorable(METERED_ACTIONS_BY_CHANNEL.mastodon),
  tiktok: authorable(METERED_ACTIONS_BY_CHANNEL.tiktok),
  reddit: authorable(METERED_ACTIONS_BY_CHANNEL.reddit),
  bluesky: authorable(METERED_ACTIONS_BY_CHANNEL.bluesky),
}

/**
 * Steps whose copy the generator writes. Everything else has no body.
 *
 * `comment_post` joined 2026-08-31 and `like_posts` deliberately did not. That
 * is the whole difference between the two verbs said in one line: a comment is
 * words somebody wrote and a person has to approve, and a reaction is a tap.
 * Membership here is what gives the step its copy box, its generated-or-custom
 * choice and its variants tab, and giving a like one would put an empty text
 * area on a step that sends nothing.
 */
/*
 * Now the kernel's own list rather than a second copy of it.
 *
 * The two were written independently and happened to agree, which is the most
 * dangerous shape for a pair like this: nothing was broken, so nothing said
 * they had drifted until the day one of them gained a verb. `carriesCopy` and
 * this constant answer the same question, and the guard beside
 * COPY_BEARING_ACTIONS asserts the answer over the WHOLE action vocabulary, so
 * a verb added later has to be classified rather than defaulting to silence.
 */
export const CONTENT_ACTIONS: readonly ActionType[] = COPY_BEARING_ACTIONS

export const ENROLLMENT_STATE: Record<EnrollmentState, StateCopy> = {
  active: { label: 'Active', detail: 'A step is due or in flight.', tone: 'accent' },
  waiting: {
    label: 'Waiting',
    detail: 'Between steps, waiting for the next one to come due.',
    tone: 'neutral',
  },
  paused_reply: {
    label: 'Paused, they replied',
    detail: 'Every channel is on hold until someone handles the reply.',
    tone: 'success',
  },
  paused_approval: {
    label: 'Waiting for approval',
    // Not "a draft is in the queue". A step waiting on templates nobody has
    // approved waits here with no draft, and the approval page counts those
    // separately.
    detail: 'The next step is waiting for a person to approve what it sends.',
    tone: 'warning',
  },
  paused_manual: { label: 'Paused by a person', detail: 'Somebody stopped this one deliberately.', tone: 'neutral' },
  snoozed: { label: 'Snoozed', detail: 'They are away, so this picks back up on their return date.', tone: 'info' },
  completed: { label: 'Finished', detail: 'The sequence ran to its end.', tone: 'neutral' },
  disqualified: { label: 'Disqualified', detail: 'Ended early. The reason is on the row.', tone: 'danger' },
}

export const ENROLLMENT_STATE_ORDER: EnrollmentState[] = [
  'active',
  'waiting',
  'paused_reply',
  'paused_approval',
  'paused_manual',
  'snoozed',
  'completed',
  'disqualified',
]

export const CAMPAIGN_STATUS: Record<CampaignStatus, StateCopy> = {
  draft: { label: 'Draft', detail: 'Set up but never launched. Nothing has sent.', tone: 'neutral' },
  running: { label: 'Running', detail: 'Sending on schedule.', tone: 'success' },
  paused: { label: 'Paused', detail: 'Nothing sends until somebody resumes it.', tone: 'warning' },
  completed: { label: 'Finished', detail: 'Everybody in it has finished. Adding people starts it again.', tone: 'neutral' },
  archived: { label: 'Archived', detail: 'Kept for the record, out of the way.', tone: 'neutral' },
}

export const PROSPECT_STATUS: Record<ProspectStatus, StateCopy> = {
  new: { label: 'New', detail: 'Imported and not yet scored against the brand.', tone: 'neutral' },
  qualified: { label: 'Qualified', detail: 'Scored and ready to go into a campaign.', tone: 'info' },
  enrolled: { label: 'Enrolled', detail: 'Currently running through a workflow.', tone: 'accent' },
  paused: { label: 'Paused', detail: 'In a campaign, but nothing is scheduled right now.', tone: 'warning' },
  replied: { label: 'Replied', detail: 'They answered, so every campaign step is on hold.', tone: 'success' },
  disqualified: {
    label: 'Disqualified',
    detail: 'Ruled out, as a poor fit or because they declined. The reason is on their record, and whether a campaign can take them depends on what still holds them.',
    tone: 'danger',
  },
  suppressed: {
    label: 'Suppressed',
    detail: 'Something makes this person off limits. The reason is on the row.',
    tone: 'danger',
  },
  converted: { label: 'Converted', detail: 'Turned into a real opportunity.', tone: 'success' },
}

/** Order shown in a filter dropdown. Mirrors the shared enum. */
export const PROSPECT_STATUS_ORDER: ProspectStatus[] = [
  'new',
  'qualified',
  'enrolled',
  'paused',
  'replied',
  'disqualified',
  'suppressed',
  'converted',
]

export const SIGNAL_TYPE: Record<SignalType, StateCopy> = {
  list_import: {
    label: 'Imported from a list',
    detail: 'Came in on a CSV or Sales Navigator export.',
    tone: 'neutral',
  },
  job_change: { label: 'Changed job', detail: 'Started a new role recently.', tone: 'success' },
  funding_round: { label: 'Raised funding', detail: 'Their company announced a round.', tone: 'success' },
  manual_tag: { label: 'Tagged', detail: 'Somebody on the team, or Discovery, flagged this person.', tone: 'info' },
  post_engagement: {
    label: 'Engaged with a post',
    detail: 'Liked or commented on a post we were watching.',
    tone: 'accent',
  },
}

/**
 * A finding Discovery wrote onto a person when a list became a sequence. It is
 * stored as a tag (company_discovery as its source), so it is named by where
 * it came from wherever the row itself is shown.
 */
export const DISCOVERY_SIGNAL: StateCopy = { label: 'Found by Discovery', detail: 'A buying signal Discovery found at their company.', tone: 'info' }

/** The words for one signal row: Discovery's own, else its kind's. */
export function signalCopy(signal: { signalType: SignalType; source?: string | null }): StateCopy {
  return signal.source === 'company_discovery' ? DISCOVERY_SIGNAL : SIGNAL_TYPE[signal.signalType]
}

export const SIGNAL_TYPE_ORDER: SignalType[] = [
  'post_engagement',
  'job_change',
  'funding_round',
  'manual_tag',
  'list_import',
]

export const SUPPRESSION_REASON: Record<SuppressionReason, StateCopy> = {
  OUR_OWN_ACCOUNT: {
    label: 'One of our own',
    detail:
      'This is one of our own people or accounts, on the protected list in Safety controls. Campaigns never contact them, in any brand.',
    tone: 'danger',
  },
  ALREADY_IN_SEQUENCE: {
    label: 'Somebody here is already writing to them',
    detail:
      'Another running campaign under this brand has this person, another campaign here wrote to them lately, they are on your list twice under two records, or a campaign set them aside until a date. This lifts on its own when that campaign finishes with them, the cooldown passes or that date passes.',
    tone: 'warning',
  },
  PRIOR_CONVERSATION: {
    label: 'Already in conversation',
    detail:
      'There is a real conversation with this person in the history, so the platform will not open a cold one. Look at the conversation before reaching out again.',
    tone: 'danger',
  },
  EXISTING_USER: {
    label: 'Already a user',
    detail:
      'This person appears in the product database, so prospecting them would be a bad look. They stay blocked while that stays true.',
    tone: 'danger',
  },
  HOSTILE_REPLY: {
    label: 'Replied hostile',
    detail:
      'A previous reply from this person was hostile, so they are permanently out of outreach. An admin can remove the block if that was a misread.',
    tone: 'danger',
  },
  UNSUBSCRIBE: {
    label: 'Opted out',
    detail: 'This person opted out. That is permanent and applies to every brand.',
    tone: 'danger',
  },
  MANUAL: {
    label: 'Blocked by hand',
    detail:
      'A teammate added this person to the suppression list. The suppressions page shows who and when.',
    tone: 'danger',
  },
  CRM_CUSTOMER: {
    label: 'Existing customer',
    detail: 'This person is on the customer list, so they are excluded from prospecting.',
    tone: 'danger',
  },
  BOUNCED: {
    label: 'Email bounced',
    detail:
      'Mail to this address bounced, so email is off the table. A verified new address would clear it.',
    tone: 'danger',
  },
  CROSS_BRAND_ACTIVE: {
    label: 'Another brand is in touch',
    detail:
      'To stop two of our brands landing in the same inbox in the same month, this person is held while the other brand has them in flight. The hold lifts on its own when the cooldown lapses.',
    tone: 'warning',
  },
}

/** The refusals the quota governor can return. */
export type QuotaReason = QuotaDeniedReason

export const QUOTA_REASON: Record<QuotaReason, StateCopy> = {
  SENDING_HALTED: {
    label: 'Sending is stopped',
    detail:
      'Somebody switched sending off. Nothing new goes to a phone or a mailbox on any channel while it is off, and anything already handed to a phone has left and cannot be called back. Turn it back on from Safety controls.',
    tone: 'danger',
  },
  DUPLICATE_ACTION: {
    label: 'The same thing was just sent',
    detail:
      'This person had the same kind of message on this channel a moment ago, so this one would arrive as a second copy of it. It is keyed on the person rather than the sender, so a second account cannot send it either. It clears itself once the window passes.',
    tone: 'warning',
  },
  FAILURE_CIRCUIT_BREAKER: {
    label: 'Stopped after failing repeatedly',
    detail:
      'This account failed too many times in a row, so the platform stopped asking rather than spending it on an eleventh failure. Usually the sign-in has gone. Fix what is failing and switch the account back on from the channel accounts page.',
    tone: 'danger',
  },
  ACCOUNT_HALTED: {
    label: 'Paused after failing repeatedly',
    detail:
      'This account failed too many times in a row, so the platform paused it for a set time rather than spending it on more failures. The pause ends on its own at the time shown, and the work waiting for it goes out then. Each pause that follows soon after the last one is longer, and after the longest the account is stopped until somebody looks at it. The accounts board shows which pause it is on.',
    tone: 'warning',
  },
  PERSON_DAILY_CEILING: {
    label: 'Daily limit reached',
    detail:
      'This sending account has used its whole allowance for today on this channel. Every account carries its own, so another account this person owns may still have room. The ceiling counts every brand together, so switching brand will not free anything up. It resets at midnight in their timezone.',
    tone: 'warning',
  },
  PERSON_WEEKLY_CEILING: {
    label: 'Weekly limit reached',
    detail:
      'This sending account has used as much of its weekly allowance on this channel as the week has released so far. The allowance is let out a little each day, unevenly, so it is not all spent in the first days of the week, and the whole of it is available by Sunday. Every account carries its own. The ceiling counts every brand together. It starts again on Monday.',
    tone: 'warning',
  },
  ACCOUNT_HEALTH: {
    label: 'Sending account is not sending',
    detail:
      'The channel account is disabled, throttled or flagged, so the platform will not put new work through it. An admin can change its state on the channel accounts page.',
    tone: 'danger',
  },
  MIN_GAP: {
    label: 'Too soon after the last action',
    detail:
      'This account has a minimum gap between actions so its behaviour stays human. The next action becomes available once that gap has passed.',
    tone: 'info',
  },
  WORKING_HOURS: {
    label: 'Outside working hours',
    detail:
      'The campaign only sends inside its configured window. The action will go out when the window next opens, or you can widen the window on the campaign.',
    tone: 'info',
  },
  DEVICE_QUIET_HOURS: {
    label: 'The phone is asleep right now',
    detail:
      'Actions run on a real handset, and it stays quiet overnight in its own local time so it behaves like a phone someone owns. This will go out when the phone next wakes up.',
    tone: 'info',
  },
  WARMUP_QUIET_PERIOD: {
    label: 'The account is too new, or too recently back, to start conversations',
    detail:
      'An account spends its first days without messaging, requesting or commenting on anybody, both when it is added and when it is switched back on or cleared after a restriction, because an identity that starts writing to strangers straight away is the pattern that gets it restricted. This goes out once those days are over. The accounts page says until when.',
    tone: 'info',
  },
  MAX_OPEN_CARDS: {
    label: 'Too many open cards',
    detail:
      'This account has hit its limit on open work cards. Clear or skip some of the queue and the platform will start creating new ones again.',
    tone: 'warning',
  },
  SESSION_LIMIT: {
    label: 'Two tools are already connected',
    detail:
      'A channel account allows two active integrations at a time. Remove one on the integrations page, or ask an admin to record an override.',
    tone: 'warning',
  },
  // The one refusal here that is not a limit doing its job. It has to say so in
  // the first sentence, because everything around it on the screen looks like a
  // limit, and an operator who reads it as one waits for a reset that will not
  // come. No blame and no instructions: this page cannot set the limits, and
  // whoever is reading it may not be the person who chooses them.
  NO_QUOTA_POLICY: {
    label: 'No limits set for this channel',
    detail:
      'Nobody has set the safe limits for this channel and action yet, so the platform will not act on it. Waiting will not change that. Someone has to choose the limits before this channel can do anything.',
    tone: 'danger',
  },
  DAILY_APP_TIME: {
    label: 'Enough time in the app for today',
    detail:
      'Each account spends only so long in an app each day, and the length is picked afresh every day so it does not stop at the same minute each time. This account has used today\'s, counting every action the phone timed, inbox checks included. It picks up again tomorrow. The accounts page shows how much it has used.',
    tone: 'info',
  },
}

// THE INBOX DESIGN'S WORDS, used everywhere a classification is named. The inbox
// was rebuilt to a design that calls these Positive, Negative and Wrong contact, and
// the inbox adopted those words. This table is also read by the dashboard and the
// needs-a-person queue, so leaving it on the older words would name one reply two
// ways depending on which screen a person was looking at.
const CLASSIFICATION: Record<ClassificationLabel, string> = {
  HOSTILE: 'Hostile',
  NEGATIVE: 'Negative',
  NEUTRAL: 'Neutral',
  QUESTION: 'Question',
  POSITIVE: 'Positive',
  OOO: 'Out of office',
  WRONG_PERSON: 'Wrong contact',
  UNSUBSCRIBE: 'Opt out',
}

const CLASSIFICATION_TONE: Record<ClassificationLabel, Tone> = {
  HOSTILE: 'danger',
  NEGATIVE: 'warning',
  NEUTRAL: 'neutral',
  QUESTION: 'info',
  POSITIVE: 'success',
  OOO: 'neutral',
  WRONG_PERSON: 'warning',
  UNSUBSCRIBE: 'danger',
}

export const OBSERVATION_TYPE: Record<ObservationType, string> = {
  connection_state: 'Connection state',
  degree: 'Degree of connection',
  last_activity: 'Last activity',
  profile_exists: 'Profile exists',
  channel_presence: 'Has an account on this channel',
  public_profile: 'What their public profile said',
  // A visit's reading of the top of the profile, not a scan of the posts, so
  // it is worded apart from the line above rather than beside it.
  profile_details: 'What their profile showed on a visit',
  open_profile: 'Takes free InMail (Open Profile)',
  instagram_visit: 'What an Instagram visit looked at',
}

/** Reads as the end of "seen when this was ...". */
export const OBSERVATION_SOURCE: Record<ObservationSource, string> = {
  manual_card: 'confirmed on a work card',
  manual_entry: 'entered by hand',
  engine: 'reported by the device engine',
  import: 'came in with an import',
}

const ACCOUNT_HEALTH: Record<EffectiveAccountHealth, string> = {
  disabled: 'Switched off',
  warming: 'Warming up',
  healthy: 'Healthy',
  throttled: 'Throttled',
  flagged: 'Flagged',
}

const ACCOUNT_HEALTH_TONE: Record<EffectiveAccountHealth, Tone> = {
  disabled: 'neutral',
  warming: 'info',
  healthy: 'success',
  throttled: 'warning',
  flagged: 'danger',
}

/**
 * How an account's work actually gets done.
 *
 * Separate from `ACCOUNT_HEALTH`, and the two are answering different
 * questions. Health is what the platform last observed about standing, so a
 * mailbox with no password sits there reading "Warming up" forever. This is
 * whether anything can be performed at all. An account can be perfectly
 * healthy and completely unreachable.
 */
export const ACCOUNT_REACH: Record<AccountReach, StateCopy> = {
  sends_itself: {
    label: 'Connected',
    detail: 'This one sends on its own, with nobody watching it.',
    tone: 'success',
  },
  by_hand: {
    label: 'You perform it',
    detail: 'Its steps arrive in your work queue as cards for you to do on the phone yourself.',
    tone: 'info',
  },
  unreachable: {
    label: 'Not connected',
    detail: 'Nothing can go out as this account until it is connected.',
    tone: 'warning',
  },
}

const OBSERVED_STATE: Record<AccountObservedState, string> = {
  warming: 'Warming up',
  healthy: 'Healthy',
  throttled: 'Throttled',
  flagged: 'Flagged',
}

/**
 * How the two sending modes are SPOKEN. Not where either of them is offered.
 *
 * The distinction matters enough to write down, because it is the one that keeps
 * this entry honest. A playbook running unattended has to be nameable on every
 * screen that shows it, so `autopilot` stays here with its real words and its
 * warning tone, and deleting it to stop people choosing it would leave a live
 * playbook rendering a raw code on its own page.
 *
 * Whether anybody may MOVE a playbook onto it is a different question with a
 * different answer, and it lives in `autopilot.ts`. Adding a key back to this
 * table has never been what puts a control on a screen, and it must not become
 * that.
 */
export const AUTONOMY: Record<Autonomy, StateCopy> = {
  copilot: {
    label: 'Approve every message',
    detail: 'Every message waits in the approval queue until a person says yes.',
    tone: 'info',
  },
  autopilot: {
    label: 'Send without approval',
    detail: 'Messages go out without a person approving each one.',
    tone: 'warning',
  },
}

const APPROVAL_STATE: Record<ApprovalState, string> = {
  'n/a': 'No approval needed',
  pending: 'Waiting for you',
  approved: 'Approved',
  rejected: 'Rejected',
}

const ASSET_TYPE: Record<AssetType, string> = {
  case_study: 'Case study',
  industry_prototype: 'Industry prototype',
  transformation_results: 'Transformation results',
  path_report: 'Assessment report',
  other: 'Other',
}

const POLICY_SCOPE: Record<PolicyScope, string> = {
  person: 'Per person',
  account: 'Per account',
  brand: 'Per brand',
  campaign: 'Per campaign',
}

const MAILBOX_KIND: Record<MailboxKind, string> = {
  outreach: 'Outreach mailbox',
  team: 'Team mailbox',
}

/**
 * Which service sends an email account's mail.
 *
 * Written as what happens rather than as a vendor list, because the difference
 * that matters to somebody reading a row is who is doing the sending: a tool
 * that holds the mailbox, or this platform signing into it.
 */
const EMAIL_PROVIDER: Record<EmailProvider, string> = {
  instantly: 'Sent by Instantly',
  autoklose: 'Sent by Autoklose',
  smtp: 'Sends as itself',
}

const DISPATCH_TARGET: Record<DispatchTarget, string> = {
  email_instantly: 'Sent by the email service',
  manual_card: 'Done by a person',
  engine: 'Done by the device engine',
  // Names what did not happen, because "dry run" alone reads to some operators
  // as a rehearsal that still went out.
  dry_run: 'Dry run, nothing sent',
}

/**
 * `label` is the chip in a branch node, `sentence` completes "if ...", which is
 * how the workflow diagram reads a condition out loud.
 */
export const DSL_CONDITION: Record<DslCondition, { label: string; sentence: string }> = {
  connected: { label: 'Connected', sentence: 'they accepted the connection' },
  not_connected: { label: 'Not connected', sentence: 'they have not connected' },
  no_reply: { label: 'No reply', sentence: 'they have not replied' },
  replied: { label: 'Replied', sentence: 'they replied' },
  email_valid: { label: 'Email is valid', sentence: 'their email address checks out' },
  // Read as the message check on email in this campaign, which is what it has
  // always meant, so it says what that check says. `conditionSentence` is what
  // every block should call. These two rows are the words for a bare code.
  not_emailed: { label: 'Not emailed yet', sentence: 'we have not emailed them yet' },
  // The sentence for the check with nothing chosen, which is any channel and
  // anyone at the brand. A block that chose reads `conditionSentence` instead.
  not_messaged: { label: 'Not messaged yet', sentence: 'we have not messaged them yet anywhere at this brand' },
  // Asked after a comment step. True when that visit found no post worth a
  // comment, so the next step can like their posts instead.
  not_commented: { label: 'Nothing to comment on', sentence: 'the comment step found nothing to comment on' },
  // The three checks over what is on file about the person. These sentences
  // are for a bare code or a check still being filled in. A check that says
  // what it looks for reads `conditionSentence`, which names it.
  has_signal: { label: 'Has a signal', sentence: 'they have a signal on file' },
  field_matches: { label: 'Field matches', sentence: 'something on file about them matches' },
  icp_score_gte: { label: 'Fit score', sentence: 'their fit score is high enough' },
  // Read from what a Luma import wrote. The two ask a fixed question, so these
  // sentences are the whole of it.
  attended_workshop: { label: 'Came to the event', sentence: 'they were checked in at a Luma event they registered for' },
  no_show: { label: 'Did not show', sentence: 'they registered for a Luma event and were not checked in' },
  // Read from the activity a profile scan dated. A block that says how many
  // days reads `conditionSentence`, which names them.
  posts_active: { label: 'Active lately', sentence: 'they have been active lately' },
  went_quiet: { label: 'Went quiet', sentence: 'they were active once and have gone quiet' },
  // Asked after a message to the person, to fall back to their company. True
  // when that message could not be sent.
  not_sent: { label: 'Message could not be sent', sentence: "the person's message could not be sent" },
  // Read from what their own lead magnet link recorded in this campaign.
  lead_magnet_opened: { label: 'Opened the lead magnet', sentence: 'they opened the lead magnet we sent them' },
  lead_magnet_clicked: { label: 'Clicked the lead magnet', sentence: 'they followed the lead magnet link we sent them' },
  lead_magnet_downloaded: { label: 'Saved the lead magnet', sentence: 'they saved a copy of the lead magnet we sent them' },
  lead_magnet_opted_in: { label: 'Signed up for the lead magnet', sentence: 'they left their email on the lead magnet page' },
}

/**
 * The checks a person can pick, in the order they are offered.
 *
 * `not_emailed` is not offered. It is the message check on email in this
 * campaign, and the picker offers that as the message check with those two
 * choices, so offering the old word too would be the same check twice under two
 * names. A stored block carrying it still reads and saves unchanged.
 */
export const DSL_CONDITION_ORDER: DslCondition[] = [
  'connected',
  'not_connected',
  'no_reply',
  'replied',
  'not_sent',
  'email_valid',
  'not_messaged',
  'not_commented',
  'has_signal',
  'field_matches',
  'icp_score_gte',
  'attended_workshop',
  'no_show',
  'posts_active',
  'went_quiet',
  'lead_magnet_clicked',
  'lead_magnet_opened',
  'lead_magnet_downloaded',
  'lead_magnet_opted_in',
]

/**
 * What a signal check says it looks for, completing "only if".
 *
 * Kept beside `SIGNAL_TYPE` rather than read from its labels, because those are
 * chip words ("Changed job") and this has to be a clause about the person.
 */
export const SIGNAL_CHECK_SENTENCE: Record<SignalType, string> = {
  job_change: 'they changed job',
  funding_round: 'their company raised funding',
  post_engagement: 'they engaged with a post we were watching',
  manual_tag: 'somebody on the team or Discovery tagged them',
  list_import: 'they came in on a list',
}

/** The fields a field check reads, as the picker names them. */
export const FIELD_CHECK_FIELD: Record<NamedFieldCheckField, string> = {
  title: 'Job title',
  industry: 'Industry',
  country: 'Country',
  research_notes: 'What research of their posts found',
}

export const FIELD_CHECK_FIELD_ORDER: NamedFieldCheckField[] = [...FIELD_CHECK_FIELDS]

/** How a field is compared, as the picker names it. */
export const FIELD_CHECK_OP: Record<FieldCheckOp, string> = {
  contains: 'Has a word starting with',
  in: 'Is exactly one of',
  equals: 'Is exactly one of',
  exists: 'Has any value',
}

export const FIELD_CHECK_OP_ORDER: FieldCheckOp[] = [...FIELD_CHECK_OPS]

/** "US or CA", "founder, ceo or cto". */
function orList(values: readonly string[]): string {
  if (values.length <= 1) return values.join('')
  return `${values.slice(0, -1).join(', ')} or ${values[values.length - 1]}`
}

/** "in the last day", "in the last 30 days". */
function lastDays(days: number): string {
  return days === 1 ? 'in the last day' : `in the last ${days} days`
}

/**
 * A check over what is on file about the person read out loud with what it
 * looks for, or null when the check is not one of those or does not say enough
 * yet to be named.
 */
function prospectCheckSentence(condition: { cond?: string | null } & ProspectCheckKeys): string | null {
  // A list still being typed carries blank lines, and a country list may be one
  // line of codes and commas. Read it the way the save will write it, so the
  // block's name does not fall back to the bare words at every keystroke.
  const listed = Array.isArray(condition.values)
    ? condition.values
        .flatMap((value) => (typeof value === 'string' && condition.field === 'country' ? value.split(/[\s,]+/) : [value]))
        .filter((value) => typeof value !== 'string' || value.trim() !== '')
    : condition.values
  const check = resolveProspectCheck(condition.cond, { ...condition, values: listed })
  if (check === null) return null
  if (check.kind === 'signal') {
    const days = check.withinDays
    const within = days === null ? '' : ` ${lastDays(days)}`
    return `${SIGNAL_CHECK_SENTENCE[check.signal]}${within}`
  }
  if (check.kind === 'score') return `their fit score is at least ${check.atLeast}`
  if (check.kind === 'luma') return DSL_CONDITION[check.asks === 'attended' ? 'attended_workshop' : 'no_show'].sentence
  if (check.kind === 'activity') {
    if (check.asks === 'active') return `they have been active ${lastDays(check.days)}`
    return `they were active once but not ${lastDays(check.days)}`
  }
  const custom = customFieldKeyOf(check.field)
  const field =
    custom !== null
      ? `{{${custom}}}`
      : check.field === 'title'
        ? 'job title'
        : check.field === 'research_notes'
          ? 'research notes'
          : check.field
  if (check.op === 'exists') return `they have a value for their ${field}`
  const values = check.values.map((value) => `"${value}"`)
  if (check.op === 'contains') return `their ${field} has a word starting with ${orList(values)}`
  return values.length === 1 ? `their ${field} is ${values[0]}` : `their ${field} is one of ${orList(values)}`
}

/** The channel a message check asks about, as the picker names it. */
export const MESSAGED_CHANNEL: Record<MessagedChannel, string> = {
  email: 'Email',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  x: 'X',
  // Messenger shares Facebook's inbox, so the check counts both and says so.
  facebook: 'Facebook or Messenger',
  telegram: 'Telegram',
  bluesky: 'Bluesky',
  any: 'Any channel',
}

/** Whose messages a message check counts, as the picker names it. */
export const MESSAGED_SCOPE: Record<MessagedScope, string> = {
  sequence: 'In this campaign',
  brand: 'From anyone at this brand',
}

/** Any channel first, because it is the default and the widest answer. */
export const MESSAGED_CHANNEL_ORDER: MessagedChannel[] = [
  'any',
  ...MESSAGED_CHANNELS.filter((channel) => channel !== 'any'),
]

export const MESSAGED_SCOPE_ORDER: MessagedScope[] = [...MESSAGED_SCOPES]

/** What "sent them" reads as for one channel, article included. */
const SENT_ON: Record<Exclude<MessagedChannel, 'email' | 'any'>, string> = {
  linkedin: 'sent them a LinkedIn message',
  instagram: 'sent them an Instagram message',
  whatsapp: 'sent them a WhatsApp message',
  x: 'sent them an X message',
  facebook: 'sent them a Facebook message',
  telegram: 'sent them a Telegram message',
  bluesky: 'sent them a Bluesky message',
}

/**
 * What a check says after "only if", with a message check's channel and scope
 * read in.
 *
 * Every place that reads a block or a step out loud should call this rather
 * than index `DSL_CONDITION`, because the table cannot know which channel a
 * message check chose. "Only if we have not messaged them yet anywhere at this
 * brand" on a check that asks about email in this campaign would tell somebody
 * the opposite of what the step does.
 */
export function conditionSentence(condition: {
  cond?: DslCondition | string | null
  channel?: unknown
  scope?: unknown
  signal?: unknown
  within_days?: unknown
  field?: unknown
  op?: unknown
  values?: unknown
  score?: unknown
}): string {
  const cond = condition.cond
  if (cond === null || cond === undefined) return ''
  // A signal, field or score check names what it looks for, for the reason the
  // message check names its channel: "only if they have a signal on file" on a
  // step that wants a job change in the last ninety days says something else.
  const named = prospectCheckSentence(condition)
  if (named !== null) return named
  const messaged = resolveMessagedCheck(cond, condition)
  if (messaged === null) return DSL_CONDITION[cond as DslCondition]?.sentence ?? humanizeCode(cond)
  const done =
    messaged.channel === 'email' ? 'emailed them' : messaged.channel === 'any' ? 'messaged them' : SENT_ON[messaged.channel]
  return `we have not ${done} yet${messaged.scope === 'brand' ? ' anywhere at this brand' : ''}`
}

const ROLE: Record<UserRole, string> = {
  admin: 'Admin',
  member: 'Member',
}

const CARD_KIND: Record<'action' | 'inbox_check' | 'connections_check', string> = {
  action: 'Outreach',
  inbox_check: 'Inbox sweep',
  connections_check: 'Connection sweep',
}

// ---------------------------------------------------------------------------
// Device actions somebody asked for by hand
// ---------------------------------------------------------------------------

/**
 * The two ways to run one.
 *
 * A rehearsal is not a preview and this copy must never sell it as one. It runs
 * the real navigation, the real gates and, for a message, the real typing, and
 * then clears the box. What it does not do is carry the action out. The one
 * thing that does reach the other person is a read receipt, because opening a
 * thread marks it read, and that is disclosed per verb by the panel that offers
 * the verb rather than flattened into one sentence here.
 */
export const OPERATOR_ACTION_MODE: Record<OperatorActionMode, StateCopy> = {
  rehearse: {
    label: 'Rehearsal',
    detail:
      'The phone walks the whole action and stops without carrying it out. Everything it checks along the way is checked for real.',
    tone: 'info',
  },
  deliver: {
    label: 'Delivery',
    detail: 'The phone walks the same steps and carries the action out at the end of them.',
    tone: 'warning',
  },
}

/**
 * Where the request has got to on this side, which is a different question from
 * what the phone made of it.
 *
 * Keeping the two apart is the point. A screen that folded them together would
 * end up calling an action finished because the phone answered, when what the
 * phone answered was that it could not tell.
 *
 * The wait in `queued` is real and this copy says so rather than implying a
 * click reaches a handset. The engine asks the platform for work and is never
 * called, so the gap is however long it is until it next asks. The "about a
 * minute" is descriptive of that long poll and gates nothing: no code reads it,
 * and no limit is set by it.
 */
export const OPERATOR_ACTION_STATE: Record<OperatorActionState, StateCopy> = {
  queued: {
    label: 'Waiting for the phone',
    detail:
      'The phone asks the platform for work on its own schedule, so this waits until it next asks. That is usually about a minute.',
    tone: 'info',
  },
  claimed: {
    label: 'Picked up by the phone',
    detail: 'The phone has taken this one and is working through it. It can no longer be cancelled.',
    tone: 'accent',
  },
  reported: {
    label: 'The phone answered',
    detail: 'The phone reported back. What it says it saw is on the row.',
    tone: 'neutral',
  },
  cancelled: {
    label: 'Cancelled',
    detail: 'Somebody cancelled this while it was still waiting, so the phone never took it.',
    tone: 'neutral',
  },
  refused: {
    label: 'Refused',
    detail: 'The platform did not put this through, and the reason is on the row.',
    tone: 'danger',
  },
}

/**
 * The sentence behind a refused action's reason code.
 *
 * A refused action carries the reservation's release reason, which is a code,
 * and the row promised "the reason is on the row", so each code the panel can
 * meet needs a sentence. `hold_expired` was the one refusal with no sentence
 * anywhere: it is what a starved handout looks like, an action handed to a
 * device that never claimed it, and an operator reading the bare code had no
 * way to know that waiting longer would not help. Codes outside this table
 * fall back to a sentence-cased version of themselves, never to silence.
 */
export const REFUSED_REASON: Record<string, string> = {
  hold_expired:
    'The action was handed out and nothing claimed it before the hold ran out, so the allowance was given back. The device may be offline or busy with another account. Nothing was performed.',
  no_engine_registered:
    'No device is registered for this identity, so there was nothing to hand the action to. The allowance was given back and nothing was performed.',
  admin_revoked: 'An admin cancelled this before the device took it. Nothing was performed.',
  suppressed_at_commit:
    'This person may no longer be contacted, and the check that holds that line refused the action at the last moment. Nothing was performed.',
  kill_switch: 'Dispatch was switched off for the whole platform while this was waiting. Nothing was performed.',
  driver_failure: 'The action could not be carried out and the allowance was given back. Nothing was performed.',
  engine_fatal: 'The device reported it could not carry this out. What it said is on the row.',
  rehearsed_on_device:
    'The phone rehearsed it. It typed the words, read them back and found the publish control, then threw the draft away. Nothing was posted and the allowance was given back.',
}

/** The sentence for a refusal code, or the code made readable when none exists. */
export function refusedReasonDetail(code: string): string {
  return REFUSED_REASON[code] ?? humanizeCode(code)
}

/**
 * The engine's own verdict, in the engine's own vocabulary.
 *
 * `unconfirmed` is the one that has to stay exactly where it is, between the two
 * others. It is neither a success nor a failure, and every convenient word for
 * it picks a side: calling it failed invites somebody to send again to a person
 * who may already have the message, and calling it sent claims a read-back that
 * did not happen. The only safe next step is to go and read the thread, and it
 * is the panel's job to offer that and nothing else.
 *
 * Unknown words fall back to a sentence-cased version of whatever the engine
 * said. A newer engine answering something this build has never seen is a
 * display problem and never a verdict this side may invent.
 */
export const OPERATOR_ACTION_RESULT: Record<KnownOperatorActionResult, StateCopy> = {
  sent: {
    label: 'Sent',
    detail: 'The phone read the message back in the thread after sending it.',
    tone: 'success',
  },
  unconfirmed: {
    label: 'Not confirmed',
    detail:
      'The phone ran the steps and could not read back an answer. Somebody has to open the thread and look at what is there.',
    tone: 'warning',
  },
  failed: {
    label: 'Failed',
    detail: 'The phone reports it could not carry this out. What it says is on the row.',
    tone: 'danger',
  },
  'dry-run': {
    label: 'Rehearsed',
    detail:
      'The phone ran the steps and did not carry the action out. Where the steps open a thread, that still marks it read on the other side.',
    tone: 'info',
  },
}

/**
 * Why a row did not become a person.
 *
 * The importer records a code and a sentence. The sentence is written for an
 * operator on every code but one: `WRITE_FAILED` carries whatever the database
 * threw, which is an internal detail and not something anybody can act on, so
 * that row gets the label alone.
 */
export const IMPORT_SKIP_REASON: Record<string, string> = {
  NO_IDENTITY: 'Nothing to identify them by',
  EMPTY_ROW: 'The row was empty',
  INVALID_EMAIL: 'The email address is not usable',
  RAGGED_ROW: 'The row does not match the header',
  WRITE_FAILED: 'The row could not be saved',
  TEAMMATE_RECORD: "It would change a teammate's address",
}

/** Codes whose message is written for the operator and worth showing under the label. */
const IMPORT_SKIP_DETAIL_CODES = new Set(['NO_IDENTITY', 'EMPTY_ROW', 'INVALID_EMAIL', 'RAGGED_ROW', 'TEAMMATE_RECORD'])

/**
 * The two lines a skipped row gets: what happened, and the specific detail when
 * there is one worth reading.
 */
export function describeImportSkip(skip: { code?: string | null; reason?: string | null }): {
  label: string
  detail: string | null
} {
  const code = skip.code ?? ''
  const label = IMPORT_SKIP_REASON[code] ?? (code === '' ? 'Skipped' : humanizeCode(code))
  const message = skip.reason ?? ''
  const detail = message !== '' && IMPORT_SKIP_DETAIL_CODES.has(code) ? message : null
  return { label, detail }
}

/**
 * The rules a sequence can break, in the words the Activity log shows.
 *
 * The audit trail keeps rule codes only, never the validator's message, because
 * a message quotes step labels and a label can carry an operator's copy. So the
 * words for each code live here, and `enum-parity.test.ts` holds this table to
 * the list the validator exports. Keyed by string because the web app does not
 * import the validator, and the parity test is what keeps the keys honest.
 */
export const SEQUENCE_RULE: Record<string, string> = {
  dsl_shape: 'The sequence is not in a shape the platform can read',
  no_steps: 'The sequence has no steps',
  no_action_steps: 'No step ever reaches anybody',
  duplicate_step_index: 'Two steps share a number',
  step_index_not_ascending: 'Step numbers are out of order',
  empty_step: 'A step does nothing',
  action_requires_channel: 'A step says what to do but not on which channel',
  channel_requires_action: 'A step names a channel but not what to do on it',
  action_channel_mismatch: 'A step asks a channel for something only another channel does',
  channel_not_enabled: 'A step uses a channel this platform does not send on yet',
  channel_cannot_perform_action: 'A channel cannot do what a step asks of it',
  entry_channel_mismatch: 'The opening channel is not where the first step sends',
  entry_step_missing: 'A way in starts at a step that does not exist',
  entry_step_not_an_action: 'A way in starts at a step that reaches nobody',
  entry_step_channel_mismatch: 'A way in names a different channel from the step it starts at',
  duplicate_entry_channel: 'Two ways in start on the same channel',
  entry_channel_not_enabled: 'A way in uses a channel this platform does not send on yet',
  entry_split_needs_two: 'A split has only one way in',
  entry_first_step_required: 'The first way in does not start at the top of the sequence',
  end_states_missing: 'The sequence never says how it ends',
  goto_target_missing: 'A branch jumps to a step that does not exist',
  goto_earlier_completed: 'A branch jumps back to a step that already ran',
  cycle_without_wait: 'Steps loop with no wait between them',
  branch_does_not_terminate: 'A path never reaches the end',
  branch_no_effect: 'A condition sends both answers to the same step',
  step_unreachable: 'A step can never be reached',
  wait_below_minimum: 'A wait is shorter than the account needs between two of the same thing',
  dm_shares_account_with_cheaper_verb: 'A message shares its account with a quicker action that keeps it waiting',
  dm_requires_connected_gate: 'A LinkedIn message is not behind a check that the person is connected',
  connection_request_after_connected: 'A connection request goes to somebody already connected',
  duplicate_connection_request: 'Two connection requests on the same path',
  follow_after_connection_request: 'A follow comes after a connection request',
  withdraw_without_request: 'A withdrawal has no connection request to withdraw',
  inmail_requires_sales_nav: 'InMail needs LinkedIn Premium or Sales Navigator',
  note_too_long: 'A connection note is longer than LinkedIn allows',
  max_messages_not_supported: 'A step that sends nothing asks for several messages',
  max_messages_above_ceiling: 'A step asks for more messages in one visit than the limit',
  email_gate_without_verifier: 'A step waits on a checked email address and nothing checks addresses',
  instagram_second_dm: 'A second Instagram message goes to somebody who has not answered the first',
  single_channel_sequence: 'The sequence only ever tries one channel',
  messenger_teammates_only: 'Messenger messages reach only teammates',
  park_on_action_step: 'A step that sets somebody aside also sends something',
  park_days_out_of_range: 'A step sets somebody aside for fewer or more days than allowed',
  copy_from_invalid_source: 'A step reuses words from a step that has none to give',
  copy_from_not_on_every_path: 'A step reuses words that some people never received',
  copy_from_not_a_message: 'A step reuses earlier words but is not a message, or has words of its own',
  channel_in_two_legs: 'Steps on one channel sit under two ways in',
  live_reply_not_a_comment: 'A step asks for words written on the post, and it is not a comment',
  split_shape: 'A split also sends, checks or ends something',
  chance_needs_action: 'A step that does nothing has a chance of running',
  goto_into_branch: 'A jump lands in the middle of another branch',
  entry_step_in_branch: 'A way in starts inside a branch',
  company_recipient_unsupported: 'A step goes to their company but is not a message',
  inmail_for_important_unsupported: 'InMail for important prospects is on a step that is not a LinkedIn message to the person',
  fit_check_not_a_follow: 'Only a follow can wait to see whether what they post fits',
  audience_not_an_instagram_view: 'Reading their audience is on a step that is not a visit to their Instagram profile',
  probability_not_supported: 'A share of people is set on a step that cannot use it',
  lead_magnet_not_a_message: 'A step carries a lead magnet but is not a message to the person',
}

/** The words for a rule, or the code in sentence case for one this table does not know yet. */
export function sequenceRuleLabel(code: string): string {
  return SEQUENCE_RULE[code] ?? humanizeCode(code)
}

/** Sentence-case fallback for a code no map covers. Never a raw enum on screen. */
export function humanizeCode(code: string): string {
  const words = code.replace(/[_-]+/g, ' ').trim().toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function lookup<K extends string>(map: Record<K, string>, key: K | null | undefined, fallback = '—'): string {
  if (key === null || key === undefined) return fallback
  return map[key] ?? humanizeCode(key)
}

function lookupCopy<K extends string>(
  map: Record<K, StateCopy>,
  key: K | null | undefined,
  fallback = '—',
): string {
  if (key === null || key === undefined) return fallback
  return map[key]?.label ?? humanizeCode(key)
}

export const label = {
  channel: (v: Channel | null | undefined) => lookup(CHANNEL, v),
  /** The same channel inside a sentence, where "email" is not a proper noun. */
  channelWord: (v: Channel | null | undefined) => lookup(CHANNEL_WORD, v),
  action: (v: ActionType | null | undefined) => lookupCopy(ACTION_TYPE, v),
  enrollmentState: (v: EnrollmentState | null | undefined) => lookupCopy(ENROLLMENT_STATE, v),
  campaignStatus: (v: CampaignStatus | null | undefined) => lookupCopy(CAMPAIGN_STATUS, v),
  prospectStatus: (v: ProspectStatus | null | undefined) => lookupCopy(PROSPECT_STATUS, v),
  classification: (v: ClassificationLabel | null | undefined) => lookup(CLASSIFICATION, v, 'Not read yet'),
  suppressionReason: (v: SuppressionReason | null | undefined) => lookupCopy(SUPPRESSION_REASON, v),
  quotaReason: (v: QuotaReason | null | undefined) => lookupCopy(QUOTA_REASON, v),
  signalType: (v: SignalType | null | undefined) => lookupCopy(SIGNAL_TYPE, v),
  observationType: (v: ObservationType | null | undefined) => lookup(OBSERVATION_TYPE, v),
  observationSource: (v: ObservationSource | null | undefined) => lookup(OBSERVATION_SOURCE, v),
  accountHealth: (v: EffectiveAccountHealth | null | undefined) => lookup(ACCOUNT_HEALTH, v),
  observedState: (v: AccountObservedState | null | undefined) => lookup(OBSERVED_STATE, v),
  autonomy: (v: Autonomy | null | undefined) => lookupCopy(AUTONOMY, v),
  approvalState: (v: ApprovalState | null | undefined) => lookup(APPROVAL_STATE, v),
  assetType: (v: AssetType | null | undefined) => lookup(ASSET_TYPE, v),
  policyScope: (v: PolicyScope | null | undefined) => lookup(POLICY_SCOPE, v),
  mailboxKind: (v: MailboxKind | null | undefined) => lookup(MAILBOX_KIND, v),
  emailProvider: (v: EmailProvider | null | undefined) => lookup(EMAIL_PROVIDER, v),
  dispatchTarget: (v: DispatchTarget | null | undefined) => lookup(DISPATCH_TARGET, v),
  dslCondition: (v: DslCondition | null | undefined) =>
    v === null || v === undefined ? '—' : (DSL_CONDITION[v]?.sentence ?? humanizeCode(v)),
  operatorActionMode: (v: OperatorActionMode | null | undefined) => lookupCopy(OPERATOR_ACTION_MODE, v),
  operatorActionState: (v: OperatorActionState | null | undefined) => lookupCopy(OPERATOR_ACTION_STATE, v),
  /**
   * The engine's word, or a sentence-cased version of it when this build has
   * never seen it. Never blank, and never this side's guess at what it meant.
   */
  operatorActionResult: (v: string | null | undefined) =>
    v === null || v === undefined
      ? '—'
      : (OPERATOR_ACTION_RESULT[v as KnownOperatorActionResult]?.label ?? humanizeCode(v)),
  role: (v: UserRole | null | undefined) => lookup(ROLE, v),
  cardKind: (v: 'action' | 'inbox_check' | 'connections_check' | null | undefined) => lookup(CARD_KIND, v),
} as const

export const tone = {
  enrollmentState: (v: EnrollmentState): Tone => ENROLLMENT_STATE[v]?.tone ?? 'neutral',
  campaignStatus: (v: CampaignStatus): Tone => CAMPAIGN_STATUS[v]?.tone ?? 'neutral',
  prospectStatus: (v: ProspectStatus): Tone => PROSPECT_STATUS[v]?.tone ?? 'neutral',
  classification: (v: ClassificationLabel): Tone => CLASSIFICATION_TONE[v] ?? 'neutral',
  accountHealth: (v: EffectiveAccountHealth): Tone => ACCOUNT_HEALTH_TONE[v] ?? 'neutral',
  signalType: (v: SignalType): Tone => SIGNAL_TYPE[v]?.tone ?? 'neutral',
  actionType: (v: ActionType): Tone => ACTION_TYPE[v]?.tone ?? 'neutral',
  autonomy: (v: Autonomy): Tone => AUTONOMY[v]?.tone ?? 'neutral',
  operatorActionState: (v: OperatorActionState): Tone => OPERATOR_ACTION_STATE[v]?.tone ?? 'neutral',
  /** A word this build does not know reads as neutral, because it is not one of ours to colour. */
  operatorActionResult: (v: string): Tone =>
    OPERATOR_ACTION_RESULT[v as KnownOperatorActionResult]?.tone ?? 'neutral',
  quotaReason: (v: QuotaReason): Tone => QUOTA_REASON[v]?.tone ?? 'warning',
  /** Suppression is always a block, so it always reads as one. */
  suppressionReason: (v: SuppressionReason): Tone => SUPPRESSION_REASON[v]?.tone ?? 'danger',
  /** Used against a ceiling: calm until it matters, loud when it is nearly gone. */
  usage: (usedRatio: number): Tone => {
    if (usedRatio >= 0.9) return 'danger'
    if (usedRatio >= 0.7) return 'warning'
    return 'accent'
  },
} as const

/** Option lists for filter dropdowns, in the order they should appear. */
export const options = {
  channel: ENABLED_CHANNEL_ORDER.map((v) => ({ value: v, label: CHANNEL[v] })),
  prospectStatus: PROSPECT_STATUS_ORDER.map((v) => ({ value: v, label: PROSPECT_STATUS[v].label })),
  enrollmentState: ENROLLMENT_STATE_ORDER.map((v) => ({ value: v, label: ENROLLMENT_STATE[v].label })),
  classification: (Object.keys(CLASSIFICATION) as ClassificationLabel[]).map((v) => ({
    value: v,
    label: CLASSIFICATION[v],
  })),
  signalType: SIGNAL_TYPE_ORDER.map((v) => ({ value: v, label: SIGNAL_TYPE[v].label })),
  suppressionReason: (Object.keys(SUPPRESSION_REASON) as SuppressionReason[]).map((v) => ({
    value: v,
    label: SUPPRESSION_REASON[v].label,
  })),
  /*
    There is deliberately no `autonomy` list here.

    It existed, it was built by walking the AUTONOMY table, and nothing in the
    console ever called it. What it did carry was a ready-made pair of options
    with "Send without approval" as the second one, which is precisely the shape
    somebody reaches for when they need a dropdown in a hurry. `autopilot.ts`
    answers who may be offered that, and it takes the platform's unlock into
    account. A convenience list here would not, and it would be the easier of the
    two to find.
  */
} as const
